import { jsx } from "react/jsx-runtime";
import { useRef } from "react";
import * as ReactPage from "../../state/react-page";
import { Props } from "../../prop-controllers";
import {
  BorderPropControllerFormat,
  BorderRadiusPropControllerFormat,
  MarginPropControllerFormat,
  PaddingPropControllerFormat,
  ShadowsPropControllerFormat,
  WidthPropControllerFormat
} from "../../prop-controllers/descriptors";
import {
  useBoxShadow,
  useResponsiveColor,
  useBorder as useBorderData
} from "../../components/hooks";
import {
  useResponsiveBorder,
  useResponsiveBorderRadius,
  useResponsiveMargin,
  useResponsivePadding,
  useResponsiveShadow,
  useResponsiveWidth
} from "../../components/utils/responsive-style";
import {
  CheckboxControlType,
  ColorControlType,
  ComboboxControlType,
  ImageControlType,
  LinkControlType,
  ListControlType,
  NumberControlType,
  SelectControlType,
  ShapeControlType,
  SlotControlType,
  StyleControlType,
  TextAreaControlType,
  TextInputControlType,
  RichTextControlType,
  RichTextV2ControlType,
  StyleV2ControlType,
  TypographyControlType
} from "../../controls";
import { useFormattedStyle } from "./controls/style";
import { ControlValue } from "./controls/control";
import { RenderHook } from "./components";
import { useSlot } from "./controls/slot";
import { useStyle } from "./use-style";
import { useRichText } from "./controls/rich-text/rich-text";
import { useRichTextV2 } from "./controls/rich-text-v2";
import { IconRadioGroupControlType } from "../../controls/icon-radio-group";
import { useStore } from "./hooks/use-store";
import { useDocumentKey } from "./hooks/use-document-key";
import { useSelector } from "./hooks/use-selector";
function useWidthStyle(value, descriptor) {
  return useStyle(useResponsiveWidth(value, descriptor.options.defaultValue));
}
function usePaddingStyle(value) {
  return useStyle(useResponsivePadding(value));
}
function useMarginStyle(value) {
  return useStyle(useResponsiveMargin(value));
}
function useBorderRadiusStyle(value) {
  return useStyle(useResponsiveBorderRadius(value));
}
function useShadowsStyle(value) {
  const shadowValue = useBoxShadow(value);
  return useStyle(useResponsiveShadow(shadowValue ?? void 0));
}
function useBorderStyle(value) {
  const borderData = useBorderData(value);
  return useStyle(useResponsiveBorder(borderData ?? void 0));
}
function PropsValue({ element, children }) {
  const store = useStore();
  const propControllerDescriptorsRef = useRef(
    ReactPage.getComponentPropControllerDescriptors(store.getState(), element.type) ?? {}
  );
  const props = element.props;
  const documentKey = useDocumentKey();
  const propControllers = useSelector((state) => {
    if (documentKey == null)
      return null;
    return ReactPage.getPropControllers(state, documentKey, element.key);
  });
  return Object.entries(propControllerDescriptorsRef.current).reduceRight(
    (renderFn, [propName, descriptor]) => (propsValue) => {
      switch (descriptor.type) {
        case CheckboxControlType:
        case NumberControlType:
        case TextInputControlType:
        case TextAreaControlType:
        case SelectControlType:
        case ColorControlType:
        case IconRadioGroupControlType:
        case ImageControlType:
        case ComboboxControlType:
        case ShapeControlType:
        case ListControlType:
        case LinkControlType:
        case StyleV2ControlType:
        case TypographyControlType:
          return /* @__PURE__ */ jsx(
            ControlValue,
            {
              definition: descriptor,
              data: props[propName],
              control: propControllers?.[propName],
              children: (value) => renderFn({ ...propsValue, [propName]: value })
            }
          );
        case StyleControlType: {
          const control = propControllers?.[propName] ?? null;
          return /* @__PURE__ */ jsx(
            RenderHook,
            {
              hook: useFormattedStyle,
              parameters: [props[propName], descriptor, control],
              children: (value) => renderFn({ ...propsValue, [propName]: value })
            },
            descriptor.type
          );
        }
        case RichTextControlType: {
          const control = propControllers?.[propName] ?? null;
          return /* @__PURE__ */ jsx(
            RenderHook,
            {
              hook: useRichText,
              parameters: [props[propName], control],
              children: (value) => renderFn({ ...propsValue, [propName]: value })
            },
            descriptor.type
          );
        }
        case RichTextV2ControlType: {
          const control = propControllers?.[propName] ?? null;
          return /* @__PURE__ */ jsx(
            RenderHook,
            {
              hook: useRichTextV2,
              parameters: [props[propName], descriptor, control],
              children: (value) => renderFn({ ...propsValue, [propName]: value })
            },
            descriptor.type
          );
        }
        case SlotControlType: {
          const control = propControllers?.[propName] ?? null;
          return /* @__PURE__ */ jsx(
            RenderHook,
            {
              hook: useSlot,
              parameters: [props[propName], control],
              children: (value) => renderFn({ ...propsValue, [propName]: value })
            },
            descriptor.type
          );
        }
        case Props.Types.Width:
          switch (descriptor.options.format) {
            case WidthPropControllerFormat.ClassName:
              return /* @__PURE__ */ jsx(
                RenderHook,
                {
                  hook: useWidthStyle,
                  parameters: [props[propName], descriptor],
                  children: (value) => renderFn({ ...propsValue, [propName]: value })
                },
                descriptor.type
              );
            default:
              return renderFn({ ...propsValue, [propName]: props[propName] });
          }
        case Props.Types.Padding:
          switch (descriptor.options.format) {
            case PaddingPropControllerFormat.ClassName:
              return /* @__PURE__ */ jsx(
                RenderHook,
                {
                  hook: usePaddingStyle,
                  parameters: [props[propName]],
                  children: (value) => renderFn({ ...propsValue, [propName]: value })
                },
                descriptor.type
              );
            default:
              return renderFn({ ...propsValue, [propName]: props[propName] });
          }
        case Props.Types.Margin:
          switch (descriptor.options.format) {
            case MarginPropControllerFormat.ClassName:
              return /* @__PURE__ */ jsx(
                RenderHook,
                {
                  hook: useMarginStyle,
                  parameters: [props[propName]],
                  children: (value) => renderFn({ ...propsValue, [propName]: value })
                },
                descriptor.type
              );
            default:
              return renderFn({ ...propsValue, [propName]: props[propName] });
          }
        case Props.Types.BorderRadius:
          switch (descriptor.options.format) {
            case BorderRadiusPropControllerFormat.ClassName:
              return /* @__PURE__ */ jsx(
                RenderHook,
                {
                  hook: useBorderRadiusStyle,
                  parameters: [props[propName]],
                  children: (value) => renderFn({ ...propsValue, [propName]: value })
                },
                descriptor.type
              );
            default:
              return renderFn({ ...propsValue, [propName]: props[propName] });
          }
        case Props.Types.Shadows:
          switch (descriptor.options.format) {
            case ShadowsPropControllerFormat.ClassName:
              return /* @__PURE__ */ jsx(
                RenderHook,
                {
                  hook: useShadowsStyle,
                  parameters: [props[propName]],
                  children: (value) => renderFn({ ...propsValue, [propName]: value })
                },
                descriptor.type
              );
            default:
              return renderFn({ ...propsValue, [propName]: props[propName] });
          }
        case Props.Types.Border:
          switch (descriptor.options.format) {
            case BorderPropControllerFormat.ClassName:
              return /* @__PURE__ */ jsx(
                RenderHook,
                {
                  hook: useBorderStyle,
                  parameters: [props[propName]],
                  children: (value) => renderFn({ ...propsValue, [propName]: value })
                },
                descriptor.type
              );
            default:
              return renderFn({ ...propsValue, [propName]: props[propName] });
          }
        case Props.Types.ResponsiveColor:
          return /* @__PURE__ */ jsx(
            RenderHook,
            {
              hook: useResponsiveColor,
              parameters: [props[propName]],
              children: (value) => renderFn({ ...propsValue, [propName]: value })
            },
            descriptor.type
          );
        default:
          return renderFn({ ...propsValue, [propName]: props[propName] });
      }
    },
    children
  )({});
}
export {
  PropsValue,
  useBorderRadiusStyle,
  useBorderStyle,
  useShadowsStyle
};
//# sourceMappingURL=controls.js.map